/**
 * API Testing Component - For debugging API connectivity issues
 * This component can be temporarily added to test API endpoints
 * Now includes VoiceLive Session Configuration display for troubleshooting
 */

import React, { useState, useEffect } from 'react';
import { testApiConnectivity, postJson, API_ENDPOINTS, buildApiUrl } from '../utils/apiUtils';
import { CloseIcon } from './SvgConsolidated/SvgIcons';

interface VoiceLiveConfig {
  voice?: { 
    name?: string; 
    type?: string; 
    temperature?: number;
    grade_voices?: { [key: string]: string | undefined };
  };
  turn_detection?: {
    type?: string;
    threshold?: number;
    silence_duration_ms?: number;
    prefix_padding_ms?: number;
    create_response?: boolean;
    [key: string]: any;
  };
  input_audio_format?: string;
  output_audio_format?: string;
  input_audio_transcription?: { model?: string };
  input_audio_noise_reduction?: { type?: string };
  input_audio_echo_cancellation?: { type?: string };
}

interface VoiceLiveStatus {
  state: string;
  conversation_state?: string;
  is_connected: boolean;
  active_sessions: number;
  current_scenario?: string;
  active_voice?: string;
}

interface SpeechSdkStatus {
  status: string;
  speech_region: string;
  endpoint_url?: string;
  api_key_present: boolean;
  api_key_length: number;
  config_valid: boolean;
  config_error: string | null;
  error_type?: string;
  message?: string;
  traceback?: string;
  proxy_configured: boolean;
  proxy_host: string | null;
  proxy_port: number | null;
  ssl_certificates_note?: string;
  egress_requirements?: string[];
  timestamp: string;
}

export const ApiTestComponent: React.FC<{ onClose?: () => void }> = ({ onClose }) => {
  const [testResults, setTestResults] = useState<{
    connectivity: {proxy: boolean, direct: boolean} | null;
    storyContextTest: string | null;
    voiceLiveConfig: VoiceLiveConfig | null;
    voiceLiveStatus: VoiceLiveStatus | null;
    speechSdkStatus: SpeechSdkStatus | null;
    error: string | null;
  }>({
    connectivity: null,
    storyContextTest: null,
    voiceLiveConfig: null,
    voiceLiveStatus: null,
    speechSdkStatus: null,
    error: null
  });
  
  const [showDetails, setShowDetails] = useState<{
    connectivity: boolean;
    storyContext: boolean;
    voiceLive: boolean;
    sessionStatus: boolean;
    speechSdk: boolean;
  }>({
    connectivity: false,
    storyContext: false,
    voiceLive: true, // Show VoiceLive config by default
    sessionStatus: true, // Show session status by default
    speechSdk: true // Show Speech SDK status by default
  });

  const runTests = async () => {
    try {
      // Test basic connectivity
      console.log('Testing API connectivity...');
      const connectivity = await testApiConnectivity();
      
      // Test story context endpoint
      let storyContextResult = null;
      try {
        const response = await postJson(API_ENDPOINTS.STORY_CONTEXT_UPDATE, {
          storyId: "story_6",
          userAge: 10,
          conversationId: "test123"
        });
        
        if (response.ok) {
          const data = await response.json();
          storyContextResult = `Success: ${data.message}`;
        } else {
          storyContextResult = `Error: ${response.status} - ${await response.text()}`;
        }
      } catch (error) {
        storyContextResult = `Exception: ${error}`;
      }

      // Fetch VoiceLive Session Configuration
      let voiceLiveConfig: VoiceLiveConfig | null = null;
      try {
        // Fix: Use buildApiUrl to handle both dev and production environments
        const configResponse = await fetch(buildApiUrl('v1/dashboard/config/voicelive'));
        if (configResponse.ok) {
          const configData = await configResponse.json();
          // Extract relevant configuration from the response
          // The API returns {voicelive_connection_config: {...}, total_settings: number}
          if (configData.voicelive_connection_config) {
            voiceLiveConfig = parseVoiceLiveConfig(configData.voicelive_connection_config);
          }
        }
      } catch (error) {
        console.error('Failed to fetch VoiceLive config:', error);
      }

      // Fetch VoiceLive Runtime Status (current session info)
      let voiceLiveStatus: VoiceLiveStatus | null = null;
      try {
        // Fix: Use buildApiUrl to handle both dev and production environments
        const statusResponse = await fetch(buildApiUrl('voicelive/status'));
        if (statusResponse.ok) {
          const statusData = await statusResponse.json();
          voiceLiveStatus = {
            state: statusData.state || 'disconnected',
            conversation_state: statusData.conversation_state,
            is_connected: statusData.is_connected || false,
            active_sessions: statusData.active_sessions || 0,
            current_scenario: statusData.current_scenario, // Use current_scenario from backend
            active_voice: statusData.active_voice // Use active_voice from backend
          };
        }
      } catch (error) {
        console.error('Failed to fetch VoiceLive status:', error);
      }

      // Fetch Speech SDK Connectivity Status
      let speechSdkStatus: SpeechSdkStatus | null = null;
      try {
        const sdkResponse = await fetch(buildApiUrl('health/speech-sdk'));
        if (sdkResponse.ok) {
          const sdkData = await sdkResponse.json();
          speechSdkStatus = {
            status: sdkData.status || 'unknown',
            speech_region: sdkData.speech_region || '',
            endpoint_url: sdkData.endpoint_url || '',
            api_key_present: sdkData.api_key_present || false,
            api_key_length: sdkData.api_key_length || 0,
            config_valid: sdkData.config_valid || false,
            config_error: sdkData.config_error || null,
            proxy_configured: sdkData.proxy_configured || false,
            proxy_host: sdkData.proxy_host || null,
            proxy_port: sdkData.proxy_port || null,
            ssl_certificates_note: sdkData.ssl_certificates_note || '',
            egress_requirements: sdkData.egress_requirements || [],
            timestamp: sdkData.timestamp || ''
          };
        }
      } catch (error) {
        console.error('Failed to fetch Speech SDK status:', error);
      }

      setTestResults({
        connectivity,
        storyContextTest: storyContextResult,
        voiceLiveConfig,
        voiceLiveStatus,
        speechSdkStatus,
        error: null
      });
      
    } catch (error) {
      setTestResults({
        connectivity: null,
        storyContextTest: null,
        voiceLiveConfig: null,
        voiceLiveStatus: null,
        speechSdkStatus: null,
        error: `Test failed: ${error}`
      });
    }
  };

  const parseVoiceLiveConfig = (settingsDict: any): VoiceLiveConfig => {
    const config: VoiceLiveConfig = {};
    
    // Helper function to get value from settings dictionary
    const getValue = (key: string) => settingsDict[key]?.value;
    
    // Parse voice settings - get all grade-specific voices
    const voiceK3 = getValue('voicelive_voice_default_grades_k_3');
    const voice45 = getValue('voicelive_voice_default_grades_4_5');
    const voice68 = getValue('voicelive_voice_default_grades_6_8');
    const voiceType = getValue('voicelive_voice_type');
    const voiceTemp = getValue('voicelive_voice_temperature_default');
    
    // Use first available voice as the default name, but store all for display
    const voiceDefault = voice68 || voice45 || voiceK3;
    
    if (voiceDefault || voiceType || voiceTemp) {
      config.voice = {
        name: voiceDefault,
        type: voiceType,
        temperature: voiceTemp ? parseFloat(voiceTemp) : undefined,
        // Store grade-specific voices for detailed display
        grade_voices: {
          'K-3': voiceK3,
          '4-5': voice45,
          '6-8': voice68
        }
      };
    }
    
    // Parse VAD settings - using conversation type as default
    const vadType = getValue('voicelive_vad_conversation_type');
    const vadThreshold = getValue('voicelive_vad_conversation_threshold');
    const vadSilence = getValue('voicelive_vad_conversation_silence_base_ms');
    const vadPadding = getValue('voicelive_vad_conversation_prefix_padding_ms');
    
    if (vadType || vadThreshold || vadSilence || vadPadding) {
      config.turn_detection = {
        type: vadType,
        threshold: vadThreshold ? parseFloat(vadThreshold) : undefined,
        silence_duration_ms: vadSilence ? parseInt(vadSilence) : undefined,
        prefix_padding_ms: vadPadding ? parseInt(vadPadding) : undefined
      };
    }
    
    // Parse audio settings
    config.input_audio_format = getValue('voicelive_audio_input_format');
    config.output_audio_format = getValue('voicelive_audio_output_format');
    
    const transcriptionModel = getValue('voicelive_audio_transcription_model');
    if (transcriptionModel) {
      config.input_audio_transcription = { model: transcriptionModel };
    }
    
    const noiseReduction = getValue('voicelive_audio_noise_reduction_type');
    if (noiseReduction) {
      config.input_audio_noise_reduction = { type: noiseReduction };
    }
    
    const echoCancellation = getValue('voicelive_audio_echo_cancellation_type');
    if (echoCancellation) {
      config.input_audio_echo_cancellation = { type: echoCancellation };
    }
    
    return config;
  };

  useEffect(() => {
    runTests();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="fixed top-20 right-4 bg-white/95 backdrop-blur-sm border border-gray-300 rounded-lg p-4 shadow-xl z-50 max-w-md max-h-[80vh] overflow-y-auto">
      <div className="flex items-center justify-between mb-3 sticky top-0 bg-white/95 pb-2">
        <h3 className="text-lg font-semibold text-gray-800">VoiceLive Debug Panel</h3>
        {onClose && (
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Close Debug Panel"
          >
            <CloseIcon className="w-4 h-4 text-gray-500" />
          </button>
        )}
      </div>
      
      {testResults.error && (
        <div className="mb-3 p-2 bg-red-100 text-red-800 rounded text-sm">
          <strong>Error:</strong> {testResults.error}
        </div>
      )}
      
      {/* VoiceLive Session Configuration */}
      {testResults.voiceLiveConfig && (
        <div className="mb-3 border border-blue-200 rounded-lg overflow-hidden">
          <button
            onClick={() => setShowDetails(prev => ({ ...prev, voiceLive: !prev.voiceLive }))}
            className="w-full flex items-center justify-between p-2 bg-blue-50 hover:bg-blue-100 transition-colors"
          >
            <h4 className="font-medium text-blue-900">🎙️ VoiceLive Session Config</h4>
            <svg 
              className={`w-4 h-4 text-blue-700 transition-transform ${showDetails.voiceLive ? 'rotate-180' : ''}`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {showDetails.voiceLive && (
            <div className="p-3 text-xs space-y-2 bg-white">
              {/* Voice Settings */}
              {testResults.voiceLiveConfig.voice && (
                <div className="bg-purple-50 p-2 rounded">
                  <div className="font-semibold text-purple-900 mb-1">🎤 Voice Configuration:</div>
                  
                  {/* Currently Active Voice (if session is running) */}
                  {testResults.voiceLiveStatus?.active_voice && (
                    <div className="mb-2 p-2 bg-purple-100 border border-purple-300 rounded">
                      <div className="text-xs font-semibold text-purple-900 mb-1">🔊 Currently Active Voice:</div>
                      <div className="text-sm font-mono text-purple-700 font-bold">
                        {testResults.voiceLiveStatus.active_voice}
                      </div>
                      <div className="text-xs text-purple-600 mt-1">
                        Voice in use for active session
                      </div>
                    </div>
                  )}
                  
                  {/* Grade-specific voices */}
                  {testResults.voiceLiveConfig.voice.grade_voices && (
                    <div className="mb-2 space-y-1">
                      <div className="text-xs font-medium text-purple-800 mb-1">Available by Grade:</div>
                      {Object.entries(testResults.voiceLiveConfig.voice.grade_voices).map(([grade, voice]) => (
                        voice && (
                          <div key={grade} className="text-gray-700 text-xs">
                            <span className="font-medium">Grades {grade}:</span>{' '}
                            <span className="font-mono text-purple-600 bg-purple-100 px-1 py-0.5 rounded">{voice}</span>
                          </div>
                        )
                      ))}
                    </div>
                  )}
                  
                  {/* Other voice settings */}
                  {testResults.voiceLiveConfig.voice.type && (
                    <div className="text-gray-700 text-xs">Type: <span className="font-mono text-purple-600">{testResults.voiceLiveConfig.voice.type}</span></div>
                  )}
                  {testResults.voiceLiveConfig.voice.temperature !== undefined && (
                    <div className="text-gray-700 text-xs">Temperature: <span className="font-mono text-purple-600">{testResults.voiceLiveConfig.voice.temperature}</span></div>
                  )}
                </div>
              )}
              
              {/* VAD Settings */}
              {testResults.voiceLiveConfig.turn_detection && (
                <div className="bg-green-50 p-2 rounded">
                  <div className="font-semibold text-green-900 mb-1">VAD Configuration:</div>
                  {testResults.voiceLiveConfig.turn_detection.type && (
                    <div className="text-gray-700">Type: <span className="font-mono text-green-600">{testResults.voiceLiveConfig.turn_detection.type}</span></div>
                  )}
                  {testResults.voiceLiveConfig.turn_detection.threshold !== undefined && (
                    <div className="text-gray-700">Threshold: <span className="font-mono text-green-600">{testResults.voiceLiveConfig.turn_detection.threshold}</span></div>
                  )}
                  {testResults.voiceLiveConfig.turn_detection.silence_duration_ms !== undefined && (
                    <div className="text-gray-700">Silence: <span className="font-mono text-green-600">{testResults.voiceLiveConfig.turn_detection.silence_duration_ms}ms</span></div>
                  )}
                  {testResults.voiceLiveConfig.turn_detection.prefix_padding_ms !== undefined && (
                    <div className="text-gray-700">Padding: <span className="font-mono text-green-600">{testResults.voiceLiveConfig.turn_detection.prefix_padding_ms}ms</span></div>
                  )}
                </div>
              )}
              
              {/* Audio Settings */}
              <div className="bg-orange-50 p-2 rounded">
                <div className="font-semibold text-orange-900 mb-1">Audio Settings:</div>
                {testResults.voiceLiveConfig.input_audio_format && (
                  <div className="text-gray-700">Input: <span className="font-mono text-orange-600">{testResults.voiceLiveConfig.input_audio_format}</span></div>
                )}
                {testResults.voiceLiveConfig.output_audio_format && (
                  <div className="text-gray-700">Output: <span className="font-mono text-orange-600">{testResults.voiceLiveConfig.output_audio_format}</span></div>
                )}
                {testResults.voiceLiveConfig.input_audio_transcription?.model && (
                  <div className="text-gray-700">Transcription: <span className="font-mono text-orange-600">{testResults.voiceLiveConfig.input_audio_transcription.model}</span></div>
                )}
                {testResults.voiceLiveConfig.input_audio_noise_reduction?.type && (
                  <div className="text-gray-700">Noise Reduction: <span className="font-mono text-orange-600">{testResults.voiceLiveConfig.input_audio_noise_reduction.type}</span></div>
                )}
                {testResults.voiceLiveConfig.input_audio_echo_cancellation?.type && (
                  <div className="text-gray-700">Echo Cancel: <span className="font-mono text-orange-600">{testResults.voiceLiveConfig.input_audio_echo_cancellation.type}</span></div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Active Session Status - Show current scenario and voice */}
      {testResults.voiceLiveStatus && (
        <div className="mb-3 border border-indigo-200 rounded-lg overflow-hidden">
          <button
            onClick={() => setShowDetails(prev => ({ ...prev, sessionStatus: !prev.sessionStatus }))}
            className="w-full flex items-center justify-between p-2 bg-indigo-50 hover:bg-indigo-100 transition-colors"
          >
            <h4 className="font-medium text-indigo-900">🔴 Active Session Status</h4>
            <svg 
              className={`w-4 h-4 text-indigo-700 transition-transform ${showDetails.sessionStatus ? 'rotate-180' : ''}`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {showDetails.sessionStatus && (
            <div className="p-3 text-xs space-y-2 bg-white">
              {/* Connection Status */}
              <div className={`p-2 rounded ${testResults.voiceLiveStatus.is_connected ? 'bg-green-50' : 'bg-gray-50'}`}>
                <div className="font-semibold text-gray-900 mb-1">Connection:</div>
                <div className={`text-xs ${testResults.voiceLiveStatus.is_connected ? 'text-green-600' : 'text-gray-600'}`}>
                  {testResults.voiceLiveStatus.is_connected ? '✓ Connected' : '○ Disconnected'}
                  {testResults.voiceLiveStatus.active_sessions > 0 && (
                    <span className="ml-2">({testResults.voiceLiveStatus.active_sessions} active session{testResults.voiceLiveStatus.active_sessions > 1 ? 's' : ''})</span>
                  )}
                </div>
              </div>
              
              {/* Current Scenario */}
              {testResults.voiceLiveStatus.is_connected && testResults.voiceLiveStatus.current_scenario && (
                <div className="bg-indigo-50 p-2 rounded">
                  <div className="font-semibold text-indigo-900 mb-1">Current Scenario:</div>
                  <div className="text-gray-700">
                    <span className="font-mono text-indigo-600 bg-indigo-100 px-2 py-1 rounded text-xs uppercase">
                      {testResults.voiceLiveStatus.current_scenario}
                    </span>
                  </div>
                  <div className="mt-1 text-xs text-gray-500">
                    {testResults.voiceLiveStatus.current_scenario === 'conversation' && '💬 Free conversation mode'}
                    {testResults.voiceLiveStatus.current_scenario === 'reading' && '📖 Story reading mode'}
                    {testResults.voiceLiveStatus.current_scenario === 'announcement' && '📢 Announcement mode'}
                    {testResults.voiceLiveStatus.current_scenario === 'quiz' && '🎮 Quiz mode'}
                  </div>
                </div>
              )}
              
              {/* Active Voice - Show which voice is being used */}
              {testResults.voiceLiveStatus.is_connected && testResults.voiceLiveStatus.active_voice && (
                <div className="bg-purple-50 p-2 rounded">
                  <div className="font-semibold text-purple-900 mb-1">Active Voice:</div>
                  <div className="text-gray-700">
                    <span className="font-mono text-purple-600 bg-purple-100 px-2 py-1 rounded text-xs">
                      {testResults.voiceLiveStatus.active_voice}
                    </span>
                  </div>
                  <div className="mt-1 text-xs text-gray-500">
                    Voice being used for current session
                  </div>
                </div>
              )}
              
              {!testResults.voiceLiveStatus.is_connected && (
                <div className="text-xs text-gray-500 text-center py-2">
                  No active VoiceLive session. Start a conversation to see runtime details.
                </div>
              )}
            </div>
          )}
        </div>
      )}
      
      {/* Speech SDK Connectivity Status */}
      {testResults.speechSdkStatus && (
        <div className="mb-3 border border-amber-200 rounded-lg overflow-hidden">
          <button
            onClick={() => setShowDetails(prev => ({ ...prev, speechSdk: !prev.speechSdk }))}
            className="w-full flex items-center justify-between p-2 bg-amber-50 hover:bg-amber-100 transition-colors"
          >
            <h4 className="font-medium text-amber-900">🎤 Speech SDK Connectivity</h4>
            <svg 
              className={`w-4 h-4 text-amber-700 transition-transform ${showDetails.speechSdk ? 'rotate-180' : ''}`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {showDetails.speechSdk && (
            <div className="p-3 text-xs space-y-2 bg-white">
              {/* Configuration Status */}
              <div className={`p-2 rounded ${testResults.speechSdkStatus.config_valid ? 'bg-green-50' : 'bg-red-50'}`}>
                <div className="font-semibold text-gray-900 mb-1">Configuration Status:</div>
                <div className={`text-xs font-mono ${testResults.speechSdkStatus.config_valid ? 'text-green-600' : 'text-red-600'}`}>
                  {testResults.speechSdkStatus.status.toUpperCase()}
                  {testResults.speechSdkStatus.config_valid ? ' ✓' : ' ✗'}
                </div>
                {testResults.speechSdkStatus.config_error && (
                  <div className="mt-1 text-xs text-red-600 bg-red-100 p-1 rounded">
                    Error: {testResults.speechSdkStatus.config_error}
                  </div>
                )}
                {testResults.speechSdkStatus.error_type && (
                  <div className="mt-1 text-xs text-red-600 bg-red-100 p-1 rounded">
                    <strong>Error Type:</strong> {testResults.speechSdkStatus.error_type}
                  </div>
                )}
                {testResults.speechSdkStatus.message && (
                  <div className="mt-1 text-xs text-red-600 bg-red-100 p-1 rounded">
                    <strong>Message:</strong> {testResults.speechSdkStatus.message}
                  </div>
                )}
                {testResults.speechSdkStatus.traceback && (
                  <div className="mt-2">
                    <details className="cursor-pointer">
                      <summary className="text-xs font-semibold text-red-900 hover:text-red-700">
                        🔍 View Full Traceback
                      </summary>
                      <pre className="mt-1 text-xs text-red-800 bg-red-50 p-2 rounded overflow-x-auto whitespace-pre-wrap border border-red-200">
                        {testResults.speechSdkStatus.traceback}
                      </pre>
                    </details>
                  </div>
                )}
              </div>
              
              {/* Region & Endpoint */}
              {testResults.speechSdkStatus.endpoint_url && (
                <div className="bg-blue-50 p-2 rounded">
                  <div className="font-semibold text-blue-900 mb-1">Region & Endpoint:</div>
                  <div className="text-gray-700">Region: <span className="font-mono text-blue-600">{testResults.speechSdkStatus.speech_region}</span></div>
                  <div className="text-gray-700 break-all">
                    <span className="font-medium">Endpoint:</span>
                    <div className="font-mono text-blue-600 text-xs mt-1 bg-blue-100 p-1 rounded">
                      {testResults.speechSdkStatus.endpoint_url}
                    </div>
                  </div>
                </div>
              )}
              
              {/* API Key Status */}
              <div className={`p-2 rounded ${testResults.speechSdkStatus.api_key_present ? 'bg-green-50' : 'bg-red-50'}`}>
                <div className="font-semibold text-gray-900 mb-1">API Key:</div>
                <div className={`text-xs ${testResults.speechSdkStatus.api_key_present ? 'text-green-600' : 'text-red-600'}`}>
                  {testResults.speechSdkStatus.api_key_present 
                    ? `✓ Present (${testResults.speechSdkStatus.api_key_length} chars)` 
                    : '✗ Missing'}
                </div>
              </div>
              
              {/* Proxy Configuration */}
              <div className={`p-2 rounded ${testResults.speechSdkStatus.proxy_configured ? 'bg-purple-50' : 'bg-gray-50'}`}>
                <div className="font-semibold text-gray-900 mb-1">Proxy Configuration:</div>
                {testResults.speechSdkStatus.proxy_configured ? (
                  <div className="text-xs text-purple-600">
                    ✓ Configured: {testResults.speechSdkStatus.proxy_host}:{testResults.speechSdkStatus.proxy_port}
                  </div>
                ) : (
                  <div className="text-xs text-gray-600">
                    Not configured (direct connection)
                  </div>
                )}
              </div>
              
              {/* Network Requirements */}
              {testResults.speechSdkStatus.egress_requirements && testResults.speechSdkStatus.egress_requirements.length > 0 && (
                <div className="bg-yellow-50 p-2 rounded">
                  <div className="font-semibold text-yellow-900 mb-1">⚠️ Network Requirements:</div>
                  <div className="text-xs text-gray-700 mb-1">
                    <strong>Egress FQDNs needed:</strong>
                  </div>
                  <ul className="list-disc list-inside space-y-0.5 text-xs text-gray-600">
                    {testResults.speechSdkStatus.egress_requirements.map((fqdn, idx) => (
                      <li key={idx} className="font-mono text-xs">{fqdn}</li>
                    ))}
                  </ul>
                  {testResults.speechSdkStatus.ssl_certificates_note && (
                    <div className="mt-2 text-xs text-gray-700">
                      <strong>SSL Certificates:</strong>
                      <div className="text-xs text-gray-600 mt-1 bg-yellow-100 p-1 rounded">
                        {testResults.speechSdkStatus.ssl_certificates_note}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Troubleshooting Tips */}
              {!testResults.speechSdkStatus.config_valid && testResults.speechSdkStatus.endpoint_url && (
                <div className="bg-red-50 border border-red-200 p-2 rounded">
                  <div className="font-semibold text-red-900 mb-1">🔧 Troubleshooting:</div>
                  <ul className="list-disc list-inside space-y-1 text-xs text-red-700">
                    <li>Verify AZURE_SPEECH_KEY is set</li>
                    <li>Verify AZURE_SPEECH_REGION matches endpoint</li>
                    <li>Check Kubernetes egress allows Speech SDK domains</li>
                    <li>Ensure Dockerfile has SSL certificates installed</li>
                    <li>Test: curl -v {testResults.speechSdkStatus.endpoint_url}</li>
                  </ul>
                </div>
              )}
              
              <div className="text-xs text-gray-400 text-center pt-1 border-t">
                Last checked: {new Date(testResults.speechSdkStatus.timestamp).toLocaleTimeString()}
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Connectivity Test */}
      {testResults.connectivity && (
        <div className="mb-3 border border-gray-200 rounded-lg overflow-hidden">
          <button
            onClick={() => setShowDetails(prev => ({ ...prev, connectivity: !prev.connectivity }))}
            className="w-full flex items-center justify-between p-2 bg-gray-50 hover:bg-gray-100 transition-colors"
          >
            <h4 className="font-medium text-gray-700">🌐 Connectivity Test</h4>
            <svg 
              className={`w-4 h-4 text-gray-600 transition-transform ${showDetails.connectivity ? 'rotate-180' : ''}`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {showDetails.connectivity && (
            <div className="p-2 text-xs bg-white">
              <div className={`${testResults.connectivity.proxy ? 'text-green-600' : 'text-red-600'}`}>
                Proxy (/api): {testResults.connectivity.proxy ? '✓ Working' : '✗ Failed'}
              </div>
              <div className={`${testResults.connectivity.direct ? 'text-green-600' : 'text-red-600'}`}>
                Direct (localhost:8000): {testResults.connectivity.direct ? '✓ Working' : '✗ Failed'}
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Story Context Test */}
      {testResults.storyContextTest && (
        <div className="mb-3 border border-gray-200 rounded-lg overflow-hidden">
          <button
            onClick={() => setShowDetails(prev => ({ ...prev, storyContext: !prev.storyContext }))}
            className="w-full flex items-center justify-between p-2 bg-gray-50 hover:bg-gray-100 transition-colors"
          >
            <h4 className="font-medium text-gray-700">📚 Story Context Test</h4>
            <svg 
              className={`w-4 h-4 text-gray-600 transition-transform ${showDetails.storyContext ? 'rotate-180' : ''}`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {showDetails.storyContext && (
            <div className={`p-2 text-xs ${testResults.storyContextTest.startsWith('Success') ? 'text-green-600' : 'text-red-600'} bg-white`}>
              {testResults.storyContextTest}
            </div>
          )}
        </div>
      )}
      
      <button 
        onClick={runTests}
        className="w-full px-3 py-2 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 transition-colors font-medium"
      >
        🔄 Refresh Config
      </button>
      
      <div className="mt-2 text-xs text-gray-500 text-center">
        Debug Panel • Remove in production
      </div>
    </div>
  );
};

export default ApiTestComponent;

// Ensure this file is treated as a module for TypeScript isolatedModules
export {};